import { Injectable } from '@angular/core';
import { Actions, State, Store, ofActionSuccessful } from '@ngxs/store';
import _ from 'lodash';
import { iif } from 'rxjs';
import { debounceTime, distinctUntilChanged, filter, groupBy, map, mergeMap, switchMap, take, tap } from 'rxjs/operators';
import { LongRunningContextCategoryTyp } from 'src/app/samwebDescription/samweb/waitingContext/waitingContextCategory';
import { SelectedSubscriberChanged } from 'src/app/store/samwin/directory/selected-subscriber/selected-subscriber.actions';
import { NextDialTargetChanged } from 'src/app/store/samwin/next-dial-target/next-dial-target.actions';
import { LongRunningContextState } from 'src/app/store/server-communication/running-actions/long-running-context.state';
import { DirectorySettingsState } from '../../../brick-settings/specific-brick-settings/directory-settings/directory-settings.state';
import {
   ClearAndStartLoadingDirectoryValues,
   DirectoryBrowserEntrySelected,
   DirectorySearchValueChanged,
   ResetDirectorySearch,
} from './directory-browser-behaviour/directory-browser-behaviour.actions';
import { DirectoryBrowserBehaviourSelectors } from './directory-browser-behaviour/directory-browser-behaviour.selectors';
import { DirectoryBrowserBehaviourState } from './directory-browser-behaviour/directory-browser-behaviour.state';
import { QueryDirectory } from './directory-data/directory-data-behaviour.actions';
import { DirectoryDataBehaviourState } from './directory-data/directory-data-behaviour.state';
import { DirectoryEditingBehaviourState } from './directory-editing/directory-editing-behaviour.state';

// tslint:disable-next-line:no-empty-interface
export interface DirectoryBehaviourStateModel {}

@State<DirectoryBehaviourStateModel>({
   name: 'directoryBehaviour',
   defaults: {},
   children: [DirectoryBrowserBehaviourState, DirectoryDataBehaviourState, DirectoryEditingBehaviourState],
})
@Injectable()
export class DirectoryBehaviourState {
   constructor(private actions$: Actions, private store: Store) {
      this.actions$
         .pipe(
            ofActionSuccessful(DirectorySearchValueChanged),
            groupBy((x: DirectorySearchValueChanged) => x.brickInstanceId),
            mergeMap((group) =>
               group.pipe(
                  debounceTime(500),
                  switchMap((action) =>
                     this.store.select(DirectoryBrowserBehaviourSelectors.getIsQueryingDirectoryPossible).pipe(
                        map((fn) => fn(action.brickInstanceId)),
                        take(1),
                        switchMap((isDirectoryQueryingPossible) =>
                           iif(
                              () => isDirectoryQueryingPossible,
                              this.store.dispatch(new ClearAndStartLoadingDirectoryValues(action.brickInstanceId)),
                              this.store.dispatch(new ResetDirectorySearch(action.brickInstanceId))
                           )
                        )
                     )
                  )
               )
            )
         )
         .subscribe();

      this.actions$
         .pipe(
            ofActionSuccessful(DirectoryBrowserEntrySelected),
            switchMap((action) =>
               this.store.select(DirectoryBrowserBehaviourSelectors.getSelectedEntry).pipe(
                  tap((getSelectedEntry) => {
                     const selectedEntry = getSelectedEntry(action.brickInstanceId);
                     if (_.isNil(selectedEntry)) {
                        this.store.dispatch(new SelectedSubscriberChanged(null, action.brickInstanceId));
                        return;
                     }
                     this.store.dispatch(new SelectedSubscriberChanged(selectedEntry, action.brickInstanceId));
                     const currentSearchValue: string[] = [this.store.selectSnapshot(DirectoryBrowserBehaviourState.getSearchValue)(action.brickInstanceId)];
                     const rowContent = this.store.selectSnapshot(DirectorySettingsState.getDefaultOrAvailableCallColumn)(
                        action.brickInstanceId,
                        selectedEntry
                     );
                     this.store.dispatch(
                        new NextDialTargetChanged(
                           {
                              number: _.isNil(selectedEntry) ? currentSearchValue.join(' ') : rowContent?.value?.toString(),
                              name: selectedEntry?.orgnam,
                              subscriberId: selectedEntry?.id,
                           },
                           action.brickInstanceId
                        )
                     );
                  })
               )
            )
         )
         .subscribe();

      this.actions$
         .pipe(
            ofActionSuccessful(QueryDirectory),
            switchMap((actions) =>
               this.store.select(LongRunningContextState.isLongRunningContextInProgressForCategories).pipe(
                  map((isSearchExecuting) => isSearchExecuting([LongRunningContextCategoryTyp.DIRECTORY_SEARCH], actions.brickInstanceId)),
                  distinctUntilChanged(),
                  filter((isSearchExecuting) => !isSearchExecuting),
                  switchMap(() =>
                     this.store.select(DirectoryBrowserBehaviourState.getSelectedDirectoryID).pipe(
                        map((getSelectedDirectoryID) => getSelectedDirectoryID(actions.brickInstanceId)),
                        distinctUntilChanged(),
                        filter((selectedDirectoryID) => _.isNil(selectedDirectoryID)),
                        switchMap(() =>
                           this.store.select(DirectoryDataBehaviourState.getFirstEntry).pipe(
                              map((getFirstEntry) => getFirstEntry(actions.brickInstanceId)),
                              distinctUntilChanged((prev, curr) => _.isEqual(prev?.id, curr?.id)),
                              filter((entry) => !_.isNil(entry)),
                              tap((entry) => this.store.dispatch(new DirectoryBrowserEntrySelected(actions.brickInstanceId, entry.id)))
                           )
                        )
                     )
                  )
               )
            )
         )
         .subscribe();
   }
}
